import type { AppearanceModule, AppearanceModulesSet } from '@setplex/tria-api'
import type { Store } from 'effector'
import type { VariantOf } from 'variant'

import { api } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEvent,
  createStore,
  restore,
  sample,
  split,
} from 'effector'
import { constant, isType, match, nil, payload, variant } from 'variant'
import { features, versioning } from '~/core/flags'
import { model as i18n } from '~/core/i18n'
import { model as guest } from '~/features/subscriber/guest'
import { nay, noop } from '~/shared/helpers'
import { lt } from '~/shared/tools/semver'
import { formatAppTheme, resolveModuleTitle } from './lib'

export const init = createEvent()

type Appearance = VariantOf<typeof Appearance>
const Appearance = variant({
  Empty: nil, // initial state, not ready yet
  Done: payload<AppearanceModulesSet>(), // successfully loaded
  Fail: nil, // failed to load
  Fall: nil, // fallback to default in case of customization disabled
})

const getAppearanceFx = attach({
  effect: api.appearance.getAppearanceFx,
})

// re-fetch appearance to refresh localized module titles only
const refreshModuleTitlesFx = attach({
  effect: api.appearance.getAppearanceFx,
})

const fallbackAppearance = createEvent()

const $appearance = createStore<Appearance>(Appearance.Empty())
  .on(getAppearanceFx.done, (_, { result }) => Appearance.Done(result))
  .on(getAppearanceFx.fail, () => Appearance.Fail())
  .on(fallbackAppearance, () => Appearance.Fall())
  .on(refreshModuleTitlesFx.doneData, (state, result) => {
    if (!isType(state, Appearance.Done)) return state
    return Appearance.Done({
      ...state.payload,
      modules: result.modules,
    })
  })
  .reset(init, api.session.signOutFx.finally)

export const $appTheme = createStore<React.CSSProperties | null>(null) //
  .reset(api.session.signOutFx.finally)

// Reset on unauthorized only when not in guest mode
sample({
  clock: api.events.http.unauthorized,
  source: guest.$enabled,
  filter: (isGuest) => nay(isGuest),
  target: [$appearance.reinit, $appTheme.reinit],
})

export const $modules: Store<AppearanceModule[] | null> = combine(
  $appearance,
  versioning.$wbsVersion,
  versioning.$isContentLocalizationEnabled,
  i18n.$t,
  (appearance, version, contentLocalizationEnabled, { t }) => {
    const modules = match(appearance, {
      Empty: constant(null),
      Done: ({ payload }) => payload.modules,
      Fail: constant(undefined),
      Fall: constant(undefined),
    })

    if (modules === null) return null // not ready yet

    const mods = modules ?? [] // it necessary to return empty array to show "No content" view

    // for wbs version < 3.18 consider 'home' module as 'static' type
    // hotfix for https://bldsoft.slack.com/archives/C0859LGMYE4/p1768835194447439
    // TODO: remove after wbs 3.18 is everywhere
    if (mods && version && lt(version, [3, 18, 0])) {
      const home = mods?.find((m) => m.identifier === 'home')
      if (home) home.type = 'static'
    }

    return mods.map((mod) => ({
      ...mod,
      title: resolveModuleTitle(mod, t, contentLocalizationEnabled),
    }))
  }
)

export const setIdentifier = createEvent<string>()
// it is set only for dynamic modules
export const $currentIdentifier = restore<string | null>(setIdentifier, null)

// ready after modules are known, from either loaded appearance or fallback
export const $ready = $modules.map(Boolean)

// get or fallback appearance on init
split({
  clock: init,
  source: features.$featuredFlags,
  match: (features) =>
    features.appearanceCustomizationEnabled ? 'get' : 'fallback',
  cases: {
    get: getAppearanceFx.prepend(noop),
    fallback: fallbackAppearance,
  },
})

// refresh only module titles after language change
sample({
  clock: i18n.$locale.updates,
  source: {
    features: features.$featuredFlags,
    appearance: $appearance,
  },
  filter: ({ features, appearance }) =>
    Boolean(features.appearanceCustomizationEnabled) &&
    isType(appearance, Appearance.Done),
  target: refreshModuleTitlesFx.prepend(noop),
})

// fill theme store if appearance is loaded
sample({
  clock: $appearance,
  filter: (appearance: Appearance) => isType(appearance, Appearance.Done),
  fn: (appearance) => formatAppTheme(appearance.payload),
  target: $appTheme,
})
